interface GradientColorGroup {
  200: string;
  300: string;
  500: string;
  700: string;
  900: string;
  950: string;
}

interface CyanColorGroup {
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

export const CYAN_COLORS: CyanColorGroup = {
  100: "#CFF9FE",
  200: "#A5F0FC",
  300: "#67E3F9",
  400: "#22CCEE",
  500: "#06AED4",
  600: "#088AB2",
  700: "#0E7090",
  800: "#155B75",
  900: "#164C63",
  950: "#0D2D3A",
};

// Ordered to maximize contrast between adjacent colors (alternating light/dark)
const CYAN_VISUALIZATION_COLORS = [
  CYAN_COLORS[700],
  CYAN_COLORS[300],
  CYAN_COLORS[500],
  CYAN_COLORS[200],
  CYAN_COLORS[800],
  CYAN_COLORS[100],
  CYAN_COLORS[900],
  CYAN_COLORS[400],
  CYAN_COLORS[950],
  CYAN_COLORS[600],
];

interface DataVisualizationColors {
  blue: GradientColorGroup;
  blueDark: GradientColorGroup;
  blueLight: GradientColorGroup;
  cyan: GradientColorGroup;
  emerald: GradientColorGroup;
  error: GradientColorGroup;
  fuchsia: GradientColorGroup;
  grayTrue: GradientColorGroup;
  green: GradientColorGroup;
  greenLight: GradientColorGroup;
  indigo: GradientColorGroup;
  moss: GradientColorGroup;
  orange: GradientColorGroup;
  orangeDark: GradientColorGroup;
  pink: GradientColorGroup;
  purple: GradientColorGroup;
  rose: GradientColorGroup;
  teal: GradientColorGroup;
  violet: GradientColorGroup;
  warning: GradientColorGroup;
  yellow: GradientColorGroup;
}

export const DATA_VISUALIZATION_COLORS: DataVisualizationColors = {
  blue: {
    200: "#b2ddff",
    300: "#84caff",
    500: "#2e90fa",
    700: "#175cd3",
    900: "#194185",
    950: "#102a56",
  },
  blueDark: {
    200: "#b2ccff",
    300: "#84adff",
    500: "#2970ff",
    700: "#004eeb",
    900: "#00359e",
    950: "#002266",
  },
  blueLight: {
    200: "#b9e6fe",
    300: "#7cd4fd",
    500: "#0ba5ec",
    700: "#026aa2",
    900: "#0b4a6f",
    950: "#062c41",
  },
  cyan: {
    200: "#A5F0FC",
    300: "#67E3F9",
    500: "#06AED4",
    700: "#0E7090",
    900: "#164C63",
    950: "#0D2D3A",
  },
  emerald: {
    200: "#a7f3d0",
    300: "#6ee7b7",
    500: "#10b981",
    700: "#047857",
    900: "#064e3b",
    950: "#022c22",
  },
  error: {
    200: "#fecdca",
    300: "#fda29b",
    500: "#f04438",
    700: "#b42318",
    900: "#7a271a",
    950: "#55160c",
  },
  fuchsia: {
    200: "#f6d0fe",
    300: "#eeaafd",
    500: "#d444f1",
    700: "#9f1ab1",
    900: "#6f1877",
    950: "#47104c",
  },
  grayTrue: {
    200: "#e5e5e5",
    300: "#d6d6d6",
    500: "#737373",
    700: "#424242",
    900: "#141414",
    950: "#0f0f0f",
  },
  green: {
    200: "#aaf0c4",
    300: "#73e2a3",
    500: "#16b364",
    700: "#087443",
    900: "#084c2e",
    950: "#052e1c",
  },
  greenLight: {
    200: "#d0f8ab",
    300: "#a6ef67",
    500: "#66c61c",
    700: "#3b7c0f",
    900: "#2b5314",
    950: "#15290a",
  },
  indigo: {
    200: "#c7d7fe",
    300: "#a4bcfd",
    500: "#6172f3",
    700: "#3538cd",
    900: "#2d3282",
    950: "#1f235b",
  },
  moss: {
    200: "#ceeab0",
    300: "#acdc79",
    500: "#669f2a",
    700: "#3f621a",
    900: "#2b4212",
    950: "#1a280b",
  },
  orange: {
    200: "#f9dbaf",
    300: "#f7b27a",
    500: "#ef6820",
    700: "#b93815",
    900: "#772917",
    950: "#511c10",
  },
  orangeDark: {
    200: "#ffd6ae",
    300: "#ff9c66",
    500: "#ff4405",
    700: "#bc1b06",
    900: "#771a0d",
    950: "#57130a",
  },
  pink: {
    200: "#fcceee",
    300: "#faa7e0",
    500: "#ee46bc",
    700: "#c11574",
    900: "#851651",
    950: "#4e0d30",
  },
  purple: {
    200: "#c5bbf7",
    300: "#a393f2",
    500: "#5f45e2",
    700: "#3e28ad",
    900: "#1b0d63",
    950: "#11064a",
  },
  rose: {
    200: "#fecdd6",
    300: "#fea3b4",
    500: "#f63d68",
    700: "#c01048",
    900: "#89123e",
    950: "#510b24",
  },
  teal: {
    200: "#99f6e0",
    300: "#5fe9d0",
    500: "#15b79e",
    700: "#107569",
    900: "#134e48",
    950: "#0a2926",
  },
  violet: {
    200: "#ddd6fe",
    300: "#c3b5fd",
    500: "#875bf7",
    700: "#6927da",
    900: "#491c96",
    950: "#2e125e",
  },
  warning: {
    200: "#fedf89",
    300: "#fec84b",
    500: "#f79009",
    700: "#b54708",
    900: "#7a2e0e",
    950: "#4e1d09",
  },
  yellow: {
    200: "#feee95",
    300: "#fde272",
    500: "#eaaa08",
    700: "#a15c07",
    900: "#713b12",
    950: "#542c0d",
  },
};

// Categorical palette — matches --redo-colors-categorical-* CSS variables
// These reference --redo-colors-*-500 primitives from primitives-colors.css
// Talk to a UX designer before changing
export const CATEGORICAL_COLORS = {
  cyan: DATA_VISUALIZATION_COLORS.cyan[500],
  warning: DATA_VISUALIZATION_COLORS.warning[500],
  pink: DATA_VISUALIZATION_COLORS.pink[500],
  greenLight: DATA_VISUALIZATION_COLORS.greenLight[500],
  error: DATA_VISUALIZATION_COLORS.error[500],
  indigo: DATA_VISUALIZATION_COLORS.indigo[500],
  orange: DATA_VISUALIZATION_COLORS.orange[500],
  blue: DATA_VISUALIZATION_COLORS.blue[500],
  teal: DATA_VISUALIZATION_COLORS.teal[500],
  orangeDark: DATA_VISUALIZATION_COLORS.orangeDark[500],
  blueDark: DATA_VISUALIZATION_COLORS.blueDark[500],
  green: DATA_VISUALIZATION_COLORS.green[500],
  purple: DATA_VISUALIZATION_COLORS.purple[500],
  moss: DATA_VISUALIZATION_COLORS.moss[500],
  violet: DATA_VISUALIZATION_COLORS.violet[500],
  blueLight: DATA_VISUALIZATION_COLORS.blueLight[500],
  emerald: DATA_VISUALIZATION_COLORS.emerald[500],
  fuchsia: DATA_VISUALIZATION_COLORS.fuchsia[500],
  yellow: DATA_VISUALIZATION_COLORS.yellow[500],
  rose: DATA_VISUALIZATION_COLORS.rose[500],
} as const;

export function getVisualizationColors(n: number): string[] {
  if (n <= 0) {
    return [];
  }
  const colors = [];
  for (let i = 0; i < n; i++) {
    colors.push(
      CYAN_VISUALIZATION_COLORS[i % CYAN_VISUALIZATION_COLORS.length],
    );
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return colors;
}

export function getVisualizationNthColor(n: number): string {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return CYAN_VISUALIZATION_COLORS[n % CYAN_VISUALIZATION_COLORS.length];
}

function getColorsForGrouping(
  gradientColorGroup: keyof DataVisualizationColors,
  n: number,
): string[] {
  if (n <= 0) {
    return [];
  } else {
    const allColorsInGradientColorGroup = [
      DATA_VISUALIZATION_COLORS[gradientColorGroup][500],
      DATA_VISUALIZATION_COLORS[gradientColorGroup][300],
      DATA_VISUALIZATION_COLORS[gradientColorGroup][200],
      DATA_VISUALIZATION_COLORS[gradientColorGroup][700],
      DATA_VISUALIZATION_COLORS[gradientColorGroup][900],
      DATA_VISUALIZATION_COLORS[gradientColorGroup][950],
    ];
    const colors = [];
    for (let i = 0; i < n; i++) {
      colors.push(
        allColorsInGradientColorGroup[i % allColorsInGradientColorGroup.length],
      );
    }
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return colors;
  }
}

/**
 * Example usage: Imagine you need to display a line chart with the following
 * 4 data series:
 * - AI (This month)
 * - AI (Last month)
 * - Human (This month)
 * - Human (Last month)
 *
 * Notice how the data series naturally fall into 2 groups/categories/clusters
 * of data. In these cases, it makes sense to group the related series with
 * colors from the same color gradient.
 *
 * @param groupings The number of groups of your data. In the example above,
 * these are "AI" and "Human"
 * @param itemsPerGroup The number of metrics in each group. In the example
 * above, these are "This month" and "Last month"
 * @returns
 */
export function getMultipleGroupingDataVisualizationColors(
  groupings: number,
  itemsPerGroup: number,
): string[][] {
  const orderedGradientColorGroups: (keyof DataVisualizationColors)[] = [
    "cyan",
    "grayTrue",
    "purple",
    "pink",
    "moss",
  ];
  const colorsInGroupings = [];
  for (let i = 0; i < groupings; i++) {
    colorsInGroupings.push(
      getColorsForGrouping(
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        orderedGradientColorGroups[i % orderedGradientColorGroups.length],
        itemsPerGroup,
      ),
    );
  }
  return colorsInGroupings;
}

export function hexToRGBA(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
